import {
  SCENARIO_COLOR_SLOTS,
  type ItemId,
  type Project,
  type Scenario,
  type ScenarioId,
} from '@finsim/model';
import { attach, createEvent, sample } from 'effector';
import { toastShown } from '../../entities/notifications';
import { $project, commandIssued, undo } from '../../entities/project';
import { scenarioInspected } from '../../entities/selection';
import { newId } from '../../shared/lib/id';

/** First colour slot not used by another variant (slots are assigned in order, never cycled). */
export function nextColor(project: Project): number {
  const used = new Set(project.scenarios.map((s) => s.color));
  for (let slot = 0; slot < SCENARIO_COLOR_SLOTS; slot++) if (!used.has(slot)) return slot;
  return project.scenarios.length % SCENARIO_COLOR_SLOTS;
}

export const addVariantRequested = createEvent<{ name: string }>();
export const duplicateVariantRequested = createEvent<{
  scenarioId: ScenarioId;
  mode: 'share' | 'copy';
  name: string;
}>();
export const deleteVariantRequested = createEvent<{ scenarioId: ScenarioId }>();
export const setBaselineRequested = createEvent<{ scenarioId: ScenarioId }>();

const addFx = attach({
  source: $project,
  effect: (project, { name }: { name: string }): Scenario => {
    if (!project) throw new Error('No project open');
    return { id: newId('var') as ScenarioId, name, color: nextColor(project), itemIds: [] };
  },
});
sample({ clock: addVariantRequested, target: addFx });
sample({
  clock: addFx.doneData,
  fn: (scenario) => ({ command: { type: 'scenario/add' as const, scenario } }),
  target: commandIssued,
});
sample({ clock: addFx.doneData, fn: (s) => s.id, target: scenarioInspected });

const duplicateFx = attach({
  source: $project,
  effect: (
    project,
    { scenarioId, mode, name }: { scenarioId: ScenarioId; mode: 'share' | 'copy'; name: string },
  ) => {
    if (!project) throw new Error('No project open');
    const source = project.scenarios.find((s) => s.id === scenarioId);
    const copies: Record<ItemId, ItemId> = {};
    if (mode === 'copy')
      for (const id of source?.itemIds ?? []) copies[id] = newId('itm') as ItemId;
    return {
      sourceId: scenarioId,
      scenario: { id: newId('var') as ScenarioId, name, color: nextColor(project) },
      copies,
    };
  },
});
sample({ clock: duplicateVariantRequested, target: duplicateFx });
sample({
  clock: duplicateFx.doneData,
  fn: (payload) => ({ command: { type: 'scenario/duplicate' as const, ...payload } }),
  target: commandIssued,
});
// Open the copy so it can be renamed straight away (it joins the compare set automatically).
sample({
  clock: duplicateFx.doneData,
  fn: ({ scenario }) => scenario.id,
  target: scenarioInspected,
});

sample({
  clock: deleteVariantRequested,
  source: $project,
  filter: Boolean,
  fn: (project, { scenarioId }) => ({
    message: 'variants.deleted' as const,
    params: { name: project.scenarios.find((s) => s.id === scenarioId)?.name ?? '' },
    action: { label: 'common.undo' as const, event: undo },
  }),
  target: toastShown,
});
sample({
  clock: deleteVariantRequested,
  fn: ({ scenarioId }) => ({ command: { type: 'scenario/remove' as const, id: scenarioId } }),
  target: commandIssued,
});

sample({
  clock: setBaselineRequested,
  fn: ({ scenarioId }) => ({ command: { type: 'scenario/setBaseline' as const, id: scenarioId } }),
  target: commandIssued,
});
